import { FC, useMemo, useState, useEffect, ChangeEvent } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { useForm, Controller, FieldError } from 'react-hook-form';
import { useSearchParams } from 'next/navigation';
import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import {
  Button,
  Modal,
  Heading,
  Link,
  Types,
  InlineMessages,
  InlineMessagesType,
  RichText,
} from '@marriott/mi-ui-library';
import {
  ReslinkEventByIdResponse,
  ReslinkType,
  getReslinkEventByIdForModifyEventQuery,
  SubmitReslinkResponse,
  submitReslinkMutation,
  ImageType,
  HotelDetails,
} from '@marriott/mi-groups-graphql';
import { Dropdown, TextFormField, CheckBoxFormField } from '../../../../molecules';
import {
  ReslinkEventDetailsFormData,
  ReslinkEventDetailsProps,
  DomainOption,
  ReslinkProductType,
  ReslinkPropertyCard,
} from './ReslinkEventDetails.types';
import { StyledReslinkEventDetails } from './ReslinkEventDetails.styles';
import { ImageTray } from './ImageTray';
import { AddHotel } from './AddHotel';
import { EventTracking } from './EventTracking';
import { EventTrackingData } from './EventTracking/EventTracking.types';
import { ReservationLinkType } from '../DashboardContentTiles/ReslinkTypeModal/ReslinkTypeModal.types';
import { useLocaleStore, useHeaderFieldsStore } from '../../../../store';
import { getEventTrackingData, getPropertyData, isGroupType } from '../../../utils/reslink';
import { getCreateReslinkEventInput } from '../../../utils/reslink';
import { UpdateEventStateModal, TextEditor } from '../../../molecules';
import { UpdateEventStateType } from '../../../molecules/UpdateEventStateModal/UpdateEventStateModal.types';
import { useReslinkAssociateStore } from '../../../store';
import { createCustomXRequestId } from '../../../../utils';

export const ReslinkEventDetailsConfig = {
  emptyLabel: 'ReslinkModifyEventDetails',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/reslink/eventdetails`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ReslinkEventDetails: FC<{ model: ReslinkEventDetailsProps }> = (props: any) => {
  const {
    heading,
    selectDomain,
    domains,
    addAssociates,
    imageTray,
    optionalProperties,
    reslinkAddHotelsModel,
    cta,
    backPopOver,
    disableEventModal,
    errorInformationMessage,
    maxRoomCardError,
    pastDateError,
    invalidPagePath,
    apiFailure,
    eventTracking,
    editorProperties,
  } = props.model;
  const {
    heading: optionalHeading,
    groupOrEventLabel,
    groupOrEventPlaceholder,
    groupOrEventHelperText,
    groupOrEventRequiredError,
    companyOrOrganizationLabel,
    companyOrOrganizationPlaceholder,
    companyOrOrganizationHelperText,
    companyOrOrganizationRequiredError,
    informationLabel,
    informationPlaceholder,
    boldAriaLabel,
    underlineAriaLabel,
    italicAriaLabel,
    eventLink1Label,
    eventLink1Placeholder,
    eventLink1RequiredError,
    eventLink1Name,
    eventLink1NamePlaceholder,
    eventLink1NameHelperText,
    eventLink1NameRequiredError,
    eventLink2Label,
    eventLink2Placeholder,
    eventLink2RequiredError,
    eventLink2Name,
    eventLink2NamePlaceholder,
    eventLink2NameHelperText,
    eventLink2NameRequiredError,
  } = optionalProperties;
  const { termsAndConditionPopUp, requiredError } = reslinkAddHotelsModel;
  const { heading: backModalHeading, stepContent, yesCtaLabel, cancelCtaLabel, backToRoomingListHubPath } = backPopOver;
  const {
    heading: eventTrackingHeading,
    isEnabled: isPickUpStatsEnabled,
    eventBookingsDetail,
    groupRates,
  } = eventTracking;

  const router = useRouter();

  const { locale } = useLocaleStore();
  const { requestId } = useHeaderFieldsStore();
  const {
    setEmailLocale,
    setEventTrackingData,
    setEventTrackingPropertyIndex,
    resetEventTrackingData,
    eventTrackingData,
  } = useReslinkAssociateStore();

  const urlParams = useSearchParams();
  const flow = urlParams.get('flow') || '';
  const eventId = urlParams.get('id') || '';
  const reslinkType = urlParams.get('reslinkType') || '';

  const isGroup = isGroupType(reslinkType as ReservationLinkType);

  const domainOptions = useMemo(
    () => domains.map((domain: DomainOption) => ({ label: domain.label, value: domain.code, locale: domain.locale })),
    [domains]
  );
  const defaultDomain = useMemo(() => domains.find((domain: DomainOption) => domain.selected) || domains[0], [domains]);

  const [isValid, setIsValid] = useState<boolean>(false);
  const [eventDescriptionModified, setEventDescriptionModified] = useState<boolean>(false);
  const [showBackToHubModal, setShowBackToHubModal] = useState<boolean>(false);
  const [showDisableEventModal, setShowDisableEventModal] = useState<boolean>(false);
  const [showTncModal, setShowTncModal] = useState<boolean>(false);
  const [images, setImages] = useState<ImageType[]>([]);
  const [uploadImageError, setUploadImageError] = useState<string>('');
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  useEffect(() => {
    setEmailLocale(locale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: eventData,
    loading,
    error: getEventByIdError,
  } = useQuery<{
    groupsReslinkEventById: ReslinkEventByIdResponse;
  }>(getReslinkEventByIdForModifyEventQuery, {
    variables: {
      input: { eventId, reslinkType: ReslinkType[reslinkType as keyof typeof ReslinkType] ?? ReslinkType.NONE },
    },
    context: {
      headers: {
        'accept-language': locale,
        'uxl-cache-disable': true,
      },
    },
    fetchPolicy: 'no-cache',
    onCompleted: ({ groupsReslinkEventById }) => {
      if (groupsReslinkEventById?.isActive && groupsReslinkEventById?.isAssociatedEvent) {
        const { associatedUsers, eventName, customImageURLs, eventDescription, eventCustomLinks, hotelDetails } =
          groupsReslinkEventById;
        const propertyCards: ReslinkPropertyCard[] = [];

        setIsValid(true);

        setValue('associates', associatedUsers?.join(';') as string);
        setValue('eventName', eventName);
        setImages(customImageURLs);
        setValue(
          'customImages',
          customImageURLs.map(({ name, link }) => ({ name, link }))
        );
        setValue('eventDescription', eventDescription);
        setValue('eventLinkName1', eventCustomLinks?.[0]?.name || '');
        setValue('eventLink1', eventCustomLinks?.[0]?.link);
        setValue('eventLinkName2', eventCustomLinks?.[1]?.name || '');
        setValue('eventLink2', eventCustomLinks?.[1]?.link);

        hotelDetails.forEach((property: HotelDetails, index: number) => {
          const propertyCard = getPropertyData(property, reslinkAddHotelsModel.propertyCard, index + 1);
          propertyCards.push(propertyCard);

          if (isGroup) {
            const miniHotelInfo = property?.miniHotelInfo;
            if (!miniHotelInfo.length) {
              setErrorMessages([pastDateError]);
            } else {
              const { availability, rates, isHousingProtected } = miniHotelInfo[0]?.node || {};
              if (!isHousingProtected) {
                const eventData = getEventTrackingData(
                  property?.property?.basicInformation?.name || '',
                  index + 1,
                  availability,
                  rates
                );

                setEventTrackingData(eventData);
              }
            }
          }
        });
        setValue('propertyCards', propertyCards);
        setEventTrackingPropertyIndex(hotelDetails?.length || 0);
      } else {
        router.push(invalidPagePath);
      }
    },
    skip: !eventId,
  });

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ReslinkEventDetailsFormData>({
    defaultValues: {
      domain: {
        label: '',
        value: '',
      },
      associates: '',
      eventName: '',
      customImages: [],
      eventDescription: '',
      eventLink1: '',
      eventLinkName1: '',
      eventLink2: '',
      eventLinkName2: '',
      propertyCards: [],
      tnc: false,
    },
    reValidateMode: 'onSubmit',
  });

  const [submitReslink, { loading: submitReslinkLoading, error: submitReslinkError }] = useMutation<{
    groupsSubmitReslink: SubmitReslinkResponse;
  }>(submitReslinkMutation);

  const imagesModified = useMemo(() => {
    return eventId
      ? getValues('customImages')?.length > 0 &&
          !isEqual(
            eventData?.groupsReslinkEventById?.customImageURLs?.map(({ name, link }) => ({ name: name, link })),
            getValues('customImages')
          )
      : getValues('customImages')?.length > 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('customImages')]);

  const closeBackToHubModal = () => {
    setShowBackToHubModal(false);
  };

  const backToRoomingListHub = () => {
    resetEventTrackingData();
    router.push(backToRoomingListHubPath);
  };

  const closeTncModal = () => {
    setShowTncModal(false);
  };

  const showTnc = (event: MouseEvent | KeyboardEvent): void => {
    event.preventDefault();
    setShowTncModal(true);
  };

  const createReslinkEvent = (data: ReslinkEventDetailsFormData) => {
    if (!getValues('propertyCards').length) {
      window.scrollTo(0, 0);
      setErrorMessages([requiredError]);
      return;
    }
    setErrorMessages([]);

    const submitReslinkInput = getCreateReslinkEventInput(isGroup, data, eventId && `${eventId}_${reslinkType}`);
    const reslinkProduct = `reslink_${reslinkType}` as ReslinkProductType;

    submitReslink({
      variables: { input: submitReslinkInput },
      context: {
        headers: {
          'accept-language': locale,
          'x-request-id': createCustomXRequestId(reslinkProduct, eventId ? 'modify' : 'create', requestId, locale),
        },
      },
      fetchPolicy: 'no-cache',
      onCompleted: ({ groupsSubmitReslink }) => {
        const { id } = groupsSubmitReslink;
        if (id) {
          const idTokens = id.split('_');
          resetEventTrackingData();
          router.push({
            pathname: cta.continuePath,
            query: { reslinkType, id: idTokens[0], flow, key: idTokens[1] },
          });
        }
      },
    });
  };

  const handleBlur = (event: ChangeEvent<HTMLInputElement>, field: { onChange: (value: string) => void }) => {
    field.onChange(event.target.value.trim());
  };

  const validateReslinkEvent = () => {
    if (!getValues('propertyCards').length) {
      window.scrollTo(0, 0);
      setErrorMessages([requiredError]);
    } else if (errors.propertyCards) {
      const propertyCardErrors = (errors.propertyCards as FieldError[])
        .filter(error => error)
        .map(error => error.message as string);
      window.scrollTo(0, 0);
      setErrorMessages([...new Set(propertyCardErrors)]);
    } else {
      setErrorMessages([]);
    }
  };

  const domainField = (
    <Controller
      name="domain"
      control={control}
      rules={{
        validate: (domain: { value: string }) => {
          if (!domain.value) {
            window.scrollTo(0, 0);
            return selectDomain.requiredError;
          }
          return true;
        },
      }}
      render={({ field }) => (
        <Dropdown
          {...field}
          label={selectDomain.label}
          ariaLabel={selectDomain.label}
          isError={!!errors.domain?.message}
          errorMessage={errors.domain?.message}
          expandIconAriaLabel={selectDomain.expandIconAriaLabel}
          collapseIconAriaLabel={selectDomain.collapseIconAriaLabel}
          selectedValue={eventId ? eventData?.groupsReslinkEventById?.localeURL : defaultDomain?.code}
          options={domainOptions}
          helperText={selectDomain.helperText}
          onChange={async (event: DomainOption) => {
            setEmailLocale(event.locale);
            field.onChange(event);
          }}
        />
      )}
    />
  );

  const associatesField = (
    <Controller
      name="associates"
      control={control}
      rules={{
        validate: associates => (associates.split(/[;,]/g).length > 11 ? addAssociates.errorText : true),
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={addAssociates.label}
          ariaLabel={addAssociates.label}
          placeholder={addAssociates.supportText}
          showErrorMessage={!!errors.associates?.message}
          errorMessage={errors.associates?.message}
          maxLength={150}
          showHelperText={true}
          helperText={addAssociates.helperText}
          className="m-input-field"
          onChange={event => {
            field.onChange(event);
          }}
        />
      )}
    />
  );

  const eventNameField = (
    <Controller
      name="eventName"
      control={control}
      rules={{
        required: isGroup ? groupOrEventRequiredError : companyOrOrganizationRequiredError,
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={isGroup ? groupOrEventLabel : companyOrOrganizationLabel}
          placeholder={isGroup ? groupOrEventPlaceholder : companyOrOrganizationPlaceholder}
          showErrorMessage={!!errors?.eventName?.message}
          errorMessage={errors?.eventName?.message}
          maxLength={100}
          showHelperText={true}
          helperText={isGroup ? groupOrEventHelperText : companyOrOrganizationHelperText}
          className="m-input-field"
          onBlur={event => handleBlur(event, field)}
        />
      )}
    />
  );

  const eventDescriptionField = (
    <Controller
      name="eventDescription"
      control={control}
      render={({ field }) => (
        <TextEditor
          {...field}
          maxLength={1000}
          placeholder={informationPlaceholder}
          content={eventData?.groupsReslinkEventById.eventDescription || ''}
          ariaLabel={informationLabel}
          boldAriaLabel={boldAriaLabel}
          underlineAriaLabel={underlineAriaLabel}
          italicAriaLabel={italicAriaLabel}
          toolbarOptions={editorProperties}
          onChange={(htmlText: string, plainText: string) => {
            field.onChange(htmlText);
            setEventDescriptionModified(!!plainText.trim());
          }}
        />
      )}
    />
  );

  const eventLink1Field = (
    <Controller
      name="eventLink1"
      control={control}
      rules={{
        required: watch('eventLinkName1') && eventLink1RequiredError,
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={eventLink1Label}
          placeholder={eventLink1Placeholder}
          showErrorMessage={!!errors?.eventLink1?.message}
          errorMessage={errors?.eventLink1?.message}
          maxLength={500}
          className="m-input-field"
          onBlur={event => handleBlur(event, field)}
        />
      )}
    />
  );

  const eventLinkName1Field = (
    <Controller
      name="eventLinkName1"
      control={control}
      rules={{
        required: watch('eventLink1') && eventLink1NameRequiredError,
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={eventLink1Name}
          placeholder={eventLink1NamePlaceholder}
          showErrorMessage={!!errors?.eventLinkName1?.message}
          errorMessage={errors?.eventLinkName1?.message}
          maxLength={40}
          showHelperText={true}
          helperText={eventLink1NameHelperText}
          className="m-input-field"
          onBlur={event => handleBlur(event, field)}
        />
      )}
    />
  );

  const eventLink2Field = (
    <Controller
      name="eventLink2"
      control={control}
      rules={{
        required: watch('eventLinkName2') && eventLink2RequiredError,
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={eventLink2Label}
          placeholder={eventLink2Placeholder}
          showErrorMessage={!!errors?.eventLink2?.message}
          errorMessage={errors?.eventLink2?.message}
          maxLength={500}
          className="m-input-field"
          onBlur={event => handleBlur(event, field)}
        />
      )}
    />
  );

  const eventLinkName2Field = (
    <Controller
      name="eventLinkName2"
      control={control}
      rules={{
        required: watch('eventLink2') && eventLink2NameRequiredError,
      }}
      render={({ field }) => (
        <TextFormField
          {...field}
          label={eventLink2Name}
          placeholder={eventLink2NamePlaceholder}
          showErrorMessage={!!errors?.eventLinkName2?.message}
          errorMessage={errors?.eventLinkName2?.message}
          maxLength={40}
          showHelperText={true}
          helperText={eventLink2NameHelperText}
          className="m-input-field"
          onBlur={event => handleBlur(event, field)}
        />
      )}
    />
  );

  const tncOption = (
    <Controller
      name="tnc"
      control={control}
      rules={{ required: termsAndConditionPopUp.requiredError }}
      render={({ field }) => (
        <CheckBoxFormField
          name="tnc"
          ariaLabel="tnc"
          options={[
            {
              label: (
                <>
                  <span>{termsAndConditionPopUp.acceptAll}</span>
                  <Link
                    text={termsAndConditionPopUp.linkText}
                    ariaLabel={termsAndConditionPopUp.linkText}
                    linkClassName="tnc-link"
                    linkHref=""
                    target="_self"
                    callback={showTnc}
                  />
                </>
              ),
              value: 'yes',
            },
          ]}
          value=""
          className="p-2 mr-4 tnc-option"
          colLength={12}
          showErrorMessage={!!errors.tnc}
          errorMessage={errors.tnc?.message || ''}
          onChange={field.onChange}
        />
      )}
    />
  );

  const showError = () => {
    window.scrollTo(0, 0);
    return (
      <div className="py-4">
        <InlineMessages type={InlineMessagesType.Error} title={apiFailure} severity="1" />
      </div>
    );
  };

  return (
    <EditableComponent config={ReslinkEventDetailsConfig} {...props}>
      {getEventByIdError || submitReslinkError ? showError() : null}
      {errorMessages.length ? (
        <div className="mt-4">
          <InlineMessages title={errorInformationMessage} type={InlineMessagesType.Error} messageList={errorMessages} />
        </div>
      ) : null}
      <StyledReslinkEventDetails data-testid="eventdetails" data-component-name="o-groups-eventdetails">
        {loading && <div className="m-spinner-m loader" />}
        {(!eventId || isValid) && (
          <>
            <Link
              text={cta.backCtaLabel}
              ariaLabel={cta.backCtaLabel}
              linkClassName="m-link-back-tertiary-button mt-4 back-cta"
              linkHref=""
              target="_self"
              callback={(event: React.FormEvent<HTMLInputElement>) => {
                event.preventDefault();
                setShowBackToHubModal(true);
              }}
            />
            {isPickUpStatsEnabled && isGroup && eventId && (
              <EventTracking
                heading={eventTrackingHeading}
                bookingStatsLabels={eventBookingsDetail}
                groupRatesLabels={groupRates}
                eventTrackingData={eventTrackingData as EventTrackingData[]}
              />
            )}
            <div className="row align-items-center justify-content-between m-0">
              <Heading
                variation={Types.headingType.title}
                element={Types.tags.h1}
                titleText={heading}
                fontSize={Types.size.medium}
                customClass="my-5"
              />
              {eventId && (
                <Button
                  buttonCopy={cta.disableEventCtaLabel}
                  className="m-button-secondary"
                  callback={() => setShowDisableEventModal(true)}
                />
              )}
            </div>
            {showDisableEventModal && (
              <UpdateEventStateModal
                labels={{ ...disableEventModal, apiFailure }}
                eventData={{ eventId: `${eventId}_${reslinkType}`, eventName: getValues('eventName') || '' }}
                updateEventStateType={UpdateEventStateType.DISABLE}
                showModal={showDisableEventModal}
                closeModal={() => setShowDisableEventModal(false)}
              />
            )}

            <form className="mb-5" onSubmit={handleSubmit(createReslinkEvent, validateReslinkEvent)}>
              <div className="mb-5 event-details">
                <div className="row">
                  <div className="col-md-4 mb-5">{domainField}</div>
                  <div className="col-md-8 mb-5">{associatesField}</div>
                </div>
                <Heading
                  variation={Types.headingType.subtitle}
                  element={Types.tags.h2}
                  titleText={optionalHeading}
                  fontSize={Types.size.extraLarge}
                  customClass="my-5"
                />
                <div className="row">
                  <div className="col-md-4 mb-5">{eventNameField}</div>
                </div>
                <div className="row mx-0">
                  <ImageTray
                    {...imageTray}
                    images={images}
                    control={control}
                    uploadImageError={uploadImageError}
                    setUploadImageError={setUploadImageError}
                  />
                </div>
                <hr className="mb-5 mt-4" />
                <div className="row">
                  <div className="col mb-5">
                    <p className="t-font-alt-s mt-2">{informationLabel}</p>
                    {eventDescriptionField}
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-8 mb-5">{eventLink1Field}</div>
                  <div className="col-md-4 mb-5">{eventLinkName1Field}</div>
                </div>
                <div className="row">
                  <div className="col-md-8 mb-5">{eventLink2Field}</div>
                  <div className="col-md-4 mb-5">{eventLinkName2Field}</div>
                </div>
              </div>

              <AddHotel
                labels={{ ...reslinkAddHotelsModel, maxRoomCardError, apiFailure }}
                control={control}
                errors={errors}
                onError={errorMessage => setErrorMessages(errorMessage ? [errorMessage] : [])}
                isPickUpStatsEnabled={isPickUpStatsEnabled && eventId}
              />
              <div className="d-flex justify-content-end my-5">
                {(eventDescriptionModified || imagesModified) && tncOption}
                <Button
                  type={Types.ButtonTypeVariation.Submit}
                  className={clsx('m-button-primary w-100', submitReslinkLoading && 'm-button skeleton-loader')}
                  isDisabled={submitReslinkLoading}
                  callback={() => uploadImageError && setUploadImageError('')}
                >
                  {cta.continueLabel}
                </Button>
              </div>
            </form>

            <Modal
              handleBlur={true}
              role="dialog"
              show={showBackToHubModal}
              popupOpenState={showBackToHubModal}
              onClose={closeBackToHubModal}
              secondaryClassName={'back-to-hub-modal'}
              setPopupOpenState={closeBackToHubModal}
              disableScrollOnBody={true}
            >
              <Modal.Header labelText={backModalHeading} popupHeaderOnCLoseFunc={closeBackToHubModal}></Modal.Header>
              <Modal.Body className="pb-5 pr-5">
                <p>{stepContent}</p>
                <div className="footer">
                  <Button
                    buttonCopy={cancelCtaLabel}
                    className="m-button-secondary mr-2"
                    callback={closeBackToHubModal}
                  />
                  <Button buttonCopy={yesCtaLabel} className="m-button-primary" callback={backToRoomingListHub} />
                </div>
              </Modal.Body>
            </Modal>

            <Modal
              handleBlur={true}
              role="dialog"
              show={showTncModal}
              popupOpenState={showTncModal}
              onClose={closeTncModal}
              setPopupOpenState={closeTncModal}
              disableScrollOnBody={true}
            >
              <Modal.Header
                labelText={termsAndConditionPopUp.headerText}
                popupHeaderOnCLoseFunc={closeTncModal}
                closeIconClickTrackValue="no tracking"
              ></Modal.Header>
              <Modal.Body className="pb-5 pr-5">
                <RichText componentId="termsAndConditionsModal" text={termsAndConditionPopUp.richText} />
              </Modal.Body>
            </Modal>
          </>
        )}
      </StyledReslinkEventDetails>
    </EditableComponent>
  );
};
